import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, FileText, BookOpen, ArrowRight, Library } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

/**
 * ChapterLibraryDialog — Sprint 1, Step 3 (Chapter-specific Libraries).
 *
 * Two panes:
 *   - Left: documents currently ATTACHED to this chapter
 *   - Right: documents in the project POOL (unassigned) → click to attach
 *
 * Generation of THIS chapter is scoped strictly to the attached library
 * (unless allow_pool_fallback is true and no docs are attached — in which
 * case the project pool is used as a safety net).
 */
export default function ChapterLibraryDialog({
  open, onOpenChange,
  userId, projectId, chapter,
  allDocuments,        // list of all project docs (for cross-chapter reassign)
  onUpdated,
}) {
  const [loading, setLoading] = useState(false);
  const [attached, setAttached] = useState([]);
  const [pool, setPool] = useState([]);
  const [otherAssigned, setOtherAssigned] = useState([]); // docs assigned to OTHER chapters
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!chapter?.id || !userId) return;
    setLoading(true);
    try {
      const d = await thesisApi.listChapterDocuments(userId, chapter.id);
      setAttached(d.attached || []);
      setPool(d.pool || []);
      // Compute docs assigned ONLY to other chapters (cross-chapter view)
      const attachedIds = new Set((d.attached || []).map(x => x.id));
      const poolIds = new Set((d.pool || []).map(x => x.id));
      const other = (allDocuments || []).filter(x =>
        !attachedIds.has(x.id) && !poolIds.has(x.id) && (x.chapter_ids || []).length > 0
      );
      setOtherAssigned(other);
    } catch (e) {
      toast.error(`Failed to load chapter library: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [userId, chapter, allDocuments]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const attach = async (doc) => {
    setSaving(true);
    try {
      const newIds = [...new Set([...(doc.chapter_ids || []), chapter.id])];
      await thesisApi.assignDocumentToChapters(userId, projectId, doc.id, newIds);
      toast.success(`Attached "${doc.title}" to "${chapter.title}"`);
      await refresh();
      onUpdated?.();
    } catch (e) {
      toast.error(`Attach failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const detach = async (doc) => {
    setSaving(true);
    try {
      const newIds = (doc.chapter_ids || []).filter(id => id !== chapter.id);
      await thesisApi.assignDocumentToChapters(userId, projectId, doc.id, newIds);
      toast.success(`Detached "${doc.title}" from this chapter`);
      await refresh();
      onUpdated?.();
    } catch (e) {
      toast.error(`Detach failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="chapter-library-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Library className="h-5 w-5 text-blue-600" />
            Chapter Library — {chapter?.title || 'Chapter'}
          </DialogTitle>
          <DialogDescription>
            Attach evidence documents to this chapter. Generation of this chapter will be <strong>strictly scoped</strong> to its attached library — never mixing with unrelated chapters' evidence.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* ATTACHED */}
            <div>
              <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
                <BookOpen className="h-4 w-4 text-emerald-600" />
                Attached to this chapter ({attached.length})
              </h3>
              <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1" data-testid="chapter-library-attached-list">
                {attached.length === 0 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 italic py-8 text-center">
                    No documents yet. Attach from the pool →
                  </p>
                )}
                {attached.map(doc => (
                  <div key={doc.id} className="rounded border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-900 px-3 py-2 group" data-testid={`chapter-library-attached-${doc.id}`}>
                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 text-emerald-700 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{doc.title}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {doc.source_type} · {doc.chunk_count || 0} chunks
                          {(doc.chapter_ids || []).length > 1 && ` · also in ${(doc.chapter_ids).length - 1} other chapter${doc.chapter_ids.length > 2 ? 's' : ''}`}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 text-red-600 hover:bg-red-100" onClick={() => detach(doc)} disabled={saving} data-testid={`chapter-library-detach-${doc.id}`}>
                        Detach
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* POOL */}
            <div>
              <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
                <Library className="h-4 w-4 text-slate-500" />
                Project pool ({pool.length}) — unassigned
              </h3>
              <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1" data-testid="chapter-library-pool-list">
                {pool.length === 0 && otherAssigned.length === 0 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 italic py-8 text-center">
                    No documents in this project. Upload from the Library tab.
                  </p>
                )}
                {pool.map(doc => (
                  <div key={doc.id} className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 hover:border-blue-300 transition" data-testid={`chapter-library-pool-${doc.id}`}>
                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{doc.title}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {doc.source_type} · {doc.chunk_count || 0} chunks
                        </p>
                      </div>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => attach(doc)} disabled={saving} data-testid={`chapter-library-attach-${doc.id}`}>
                        <ArrowRight className="h-3 w-3 mr-1" />Attach
                      </Button>
                    </div>
                  </div>
                ))}

                {otherAssigned.length > 0 && (
                  <details className="pt-3">
                    <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
                      Assigned to other chapters ({otherAssigned.length}) — click to also attach here
                    </summary>
                    <div className="mt-2 space-y-1">
                      {otherAssigned.map(doc => (
                        <div key={doc.id} className="rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 px-3 py-2 hover:border-blue-300 transition">
                          <div className="flex items-start gap-2">
                            <FileText className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{doc.title}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                In {(doc.chapter_ids || []).length} other chapter{(doc.chapter_ids || []).length > 1 ? 's' : ''}
                              </p>
                            </div>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => attach(doc)} disabled={saving}>
                              <ArrowRight className="h-3 w-3 mr-1" />Also Attach
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-3 border-t border-slate-200 dark:border-slate-700">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="chapter-library-close">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
